/*
 * Almila accessibility helper.
 *
 * tiny-slider (slider.js) marks off-screen slides and clones aria-hidden="true" and tabindex="-1" on the slide
 * itself, but links and buttons inside the slide stay in the tab order. That is an axe aria-hidden-focus
 * failure and sends keyboard users to controls they cannot see. This helper keeps every focusable descendant of
 * an aria-hidden slide out of the tab order and restores it as soon as the slide becomes visible again.
 *
 * It works from the aria-hidden attribute alone, so it covers every tiny-slider instance (product gallery,
 * recommendations, curated products, home carousels) without editing the vendor files. Slides stay clickable
 * with a mouse or touch; only keyboard focus is affected.
 *
 * It also turns the hCaptcha badge that Shopify injects on the customer forms into a labelled region (using the
 * badge's own localized text), so its links are not content outside every landmark.
 *
 * And it makes the theme's product accordion (snippets/product-accordian.liquid, the delivery information) work from
 * the keyboard: its header is a span that takes focus but answers only a mouse click. The header gets the button
 * role and its open state, and Enter and Space do what a click does.
 *
 * And it lets Tab follow the page from the skip link (12 Oct 2026, audit K8). The theme sends Tab from the skip link
 * straight to the logo (assets/accessibility.js). That jump hid three things that stand before the logo in the page,
 * and with them every control there: the top bar's links and the search field, and on phones the menu and search
 * buttons. The three causes are removed here and the jump is cancelled:
 * - the cart drawer (snippets/mini-cart.liquid, right after the skip link) is off screen while closed but its
 *   controls took focus: they leave the Tab order until body.show-sidebar opens it;
 * - the header's own containers carry tabindex="0" (snippets/header-center.liquid, header-left.liquid), three stops
 *   on things that do nothing: the attribute is taken off;
 * - Tab on the skip link is kept from the theme's handler, forward and back.
 * Without this file the theme's order remains: skip link, then the logo.
 *
 * Four more things the theme leaves to the keyboard (13 Oct 2026, TODO M4 to M7):
 * - a video in a hidden slide kept its Tab stops, and focusing it dragged the gallery sideways: videos and audio
 *   are in the list of things a hidden slide keeps out of the order;
 * - slim-select (the sort list, the product page's option list) is worked from the keyboard through a search field
 *   of no height: focus on it opens the list, the arrows walk it and Enter chooses. It was the Tab stop, announced
 *   as "search in the options", so the list opened by itself whenever Tab came by (over the quantity and the buy
 *   button on the product page), and after a choice or Escape focus was on nothing: the next Space scrolled the
 *   page. Since 13 Oct 2026 the visible field is the stop, a combobox named after its list (on the product page the
 *   option's own caption, which the theme's label does not tie to the list): Enter, Space or an arrow opens the
 *   list by handing focus to that search field, which is no longer a stop itself; the list opens on the current
 *   row; while it is open the search field is the combobox a screen reader hears, its active descendant the row
 *   under the arrows (each row has an id and aria-selected); after Enter or Escape focus is back on the visible
 *   field; Tab and Shift+Tab close the list and move on. An open list that reaches below the window is brought into
 *   view, and so is the row under the arrows. The field shows a ring while it holds focus from the keyboard
 *   (almila-a11y.css; a pointer press marks <html> until Tab is used again). Since 14 Oct 2026 a closed list is out
 *   of the accessibility tree (aria-hidden on its panel; the library only scaled it to nothing);
 * - on the product page the theme redirects Tab around its own gallery layout; with ours Shift+Tab went round and
 *   round inside the gallery. Tab pressed inside the gallery is kept from the theme's handler;
 * - the cart drawer, while open, is a dialog: named, it takes focus when it opens (the theme's own line for that
 *   never runs), Tab stays inside it, Escape and its close mark (a span made a button) close it, and focus returns
 *   to where it was. The theme closed the drawer whenever focus left the tax note at its end and then looked for a
 *   menu item that is not on this page (two errors per Tab). Focus is moved only once an event has run to its end:
 *   on every mouseup the theme takes the drawer's class off and, for a mouseup inside the drawer, puts it back
 *   (app.js closeCartDrawer, keepCartOpen), so the drawer looks closed for a moment in the middle of a click.
 *
 * And it keeps the theme's back-to-top button (#back-top, layout/theme.liquid) off what a reader needs (14 Oct 2026).
 * Fixed 52px over the bottom right corner, on touch screens it covered the right end of whatever passed there: the
 * last line of a text, the right card's cart button. It steps aside while the page moves down and comes back once the
 * page has moved up; the hiding itself (touch screens and windows up to 991px) is in almila-page-layout.css.
 *
 * And the theme's two card rows on tiny-slider (14 Oct 2026, seen by the review of 13 Oct): the promotion page's
 * featured collection (sections/main-featured-collection.liquid) and the product page's complementary products
 * (sections/complementary-recomendations.liquid, assets/recommendations.js). The slider marks the cards outside the
 * view aria-hidden, so the start of this file keeps their controls out of the Tab order, and it gives the arrows
 * tabindex="-1": a keyboard reached only the cards in view. Now focus that arrives in a row from the keyboard brings
 * its card into the row's box, moved by the row's own arrows; Tab on the last stop of the last card in view goes on
 * to the next card (Shift+Tab, the previous one) and the row follows. The complementary row's arrow box, which the
 * slider made a Tab stop of no height, is not a stop. Pointer and touch use are unchanged.
 */
(function () {
  'use strict';
  // One observer per page: the theme editor runs this file again when the header section re-renders.
  if (window.almilaA11yReady) return;
  window.almilaA11yReady = true;

  var SLIDE = '.tns-item';
  var FOCUSABLE = 'a[href], area[href], button, input, select, textarea, summary, iframe, video, audio, [tabindex], [contenteditable="true"]';
  var SAVED = 'data-almila-tabindex';
  var NO_TABINDEX = 'none';
  var BADGE = 'shop-hcaptcha-badge-container';
  var ACCORDION = '.product-accordion-item > .faq-title';
  var accordions = 0;
  var CART = 'aside.sidebar';
  var CART_OPEN = 'show-sidebar';
  var HEADER_STOPS = '[data-section-type="header"][tabindex="0"], [data-section-type="header"] div[tabindex="0"], [data-section-type="header"] section[tabindex="0"]';
  var SKIP_LINK = 'skip-to-content-link';
  var HIDDEN_SEARCH = '.ss-search.ss-hide input';
  var GALLERY = '#product-image';
  var CART_CLOSE = '.js-close-cart';
  var CART_NOTE = '.tax-note';
  var OPTION_ROW = '.product-option-row';
  var SELECT_FIELD = '.ss-single-selected';
  var SELECT_PANEL = '.ss-content';
  var SELECT_LIST = '.ss-list';
  var SELECT_OPTION = '.ss-option';
  // The class slim-select puts on its panel while the list is open (its config.open).
  var SELECT_OPEN = 'ss-open';
  // Room kept above the field, under whatever is fixed or sticky at the top, when an open list is brought into view.
  var SELECT_GAP = 16;
  var selects = 0;
  var POINTER = 'data-almila-pointer';
  // The theme's two card rows (see the top of this file): the featured collection's container is the row itself,
  // the complementary products' container sits inside its section.
  var CARD_ROW = '.featured-collection.tns-slider, .product-complementary .tns-slider';
  // The script's own tag carries the one text it needs (sections/header.liquid).
  var closeCartLabel = document.currentScript ? document.currentScript.getAttribute('data-close-cart') : '';
  var cartWasOpen = false;
  var cartSettling = false;
  var lastOutsideCart = null;
  var BACK_TOP = 'back-top';
  var BACK_TOP_AWAY = 'almila-back-top-away';
  // How far the page has to move up before the back-to-top button returns: more than a finger's jitter.
  var BACK_TOP_RETURN = 24;

  function hide(slide) {
    slide.querySelectorAll(FOCUSABLE).forEach(function (el) {
      if (el.hasAttribute(SAVED)) {
        return;
      }
      el.setAttribute(SAVED, el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : NO_TABINDEX);
      el.setAttribute('tabindex', '-1');
    });
  }

  function show(slide) {
    slide.querySelectorAll('[' + SAVED + ']').forEach(function (el) {
      var saved = el.getAttribute(SAVED);
      if (saved === NO_TABINDEX) {
        el.removeAttribute('tabindex');
      } else {
        el.setAttribute('tabindex', saved);
      }
      el.removeAttribute(SAVED);
    });
  }

  function inClosedCart(element) {
    return !document.body.classList.contains(CART_OPEN) && !!element.closest(CART);
  }

  function sync(slide) {
    if (slide.getAttribute('aria-hidden') === 'true' || inClosedCart(slide)) {
      hide(slide);
    } else {
      show(slide);
    }
  }

  function labelBadge(badge) {
    var text = badge.querySelector('.info > span');
    if (text && !badge.hasAttribute('role')) {
      badge.setAttribute('role', 'region');
      badge.setAttribute('aria-label', text.textContent.trim());
    }
  }

  function expanded(title) {
    var open = title.classList.contains('active');
    var panel = title.nextElementSibling;
    title.setAttribute('aria-expanded', open ? 'true' : 'false');
    // The theme only clips a closed panel (max-height 0), so its links stayed in the Tab order and were read out.
    if (panel) {
      panel.inert = !open;
    }
  }

  function accordion(title) {
    var panel = title.nextElementSibling;
    if (title.getAttribute('role') === 'button') {
      return;
    }
    title.setAttribute('role', 'button');
    expanded(title);
    if (panel) {
      accordions += 1;
      panel.id = panel.id || 'almila-accordion-' + accordions;
      title.setAttribute('aria-controls', panel.id);
    }
    title.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
      title.click();
    });
    title.addEventListener('click', function () {
      // The theme's own click handler toggles the class; read it once every handler has run.
      setTimeout(function () {
        expanded(title);
      }, 0);
    });
  }

  function cartIsOpen(cart) {
    return !!cart && document.body.classList.contains(CART_OPEN) && cart.getClientRects().length > 0;
  }

  // As the theme's own two handlers on the close mark do (app.js): its handlers are bound only once the theme has
  // opened the drawer itself, so the class is taken off here as well.
  function closeCart(cart) {
    var closer = cart.querySelector(CART_CLOSE);
    if (closer) {
      closer.click();
    }
    document.body.classList.remove(CART_OPEN);
  }

  function cartControls(cart) {
    return Array.prototype.filter.call(cart.querySelectorAll(FOCUSABLE), function (el) {
      return el.tabIndex >= 0 && el.getClientRects().length > 0;
    });
  }

  // The open drawer is a dialog: named by its own title, its close mark a button, its note plain text again.
  function dressCart(cart) {
    var title = cart.querySelector('.js-cart-data-title');
    var closer = cart.querySelector(CART_CLOSE);
    cart.setAttribute('role', 'dialog');
    cart.setAttribute('aria-modal', 'true');
    if (title) {
      title.id = title.id || 'AlmilaCartTitle';
      cart.setAttribute('aria-labelledby', title.id);
    }
    if (closer && closer.getAttribute('role') !== 'button') {
      closer.setAttribute('role', 'button');
      closer.setAttribute('tabindex', '0');
      if (closeCartLabel) {
        closer.setAttribute('aria-label', closeCartLabel);
      }
      closer.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          closeCart(cart);
        }
      });
    }
    // The theme made the tax note a Tab stop to close the drawer when focus left it; it is text.
    cart.querySelectorAll(CART_NOTE + '[tabindex="0"]').forEach(function (note) {
      note.setAttribute('tabindex', '-1');
    });
  }

  // The cart drawer: out of the Tab order while it is closed, back in when the theme opens it.
  function syncCart() {
    var cart = document.querySelector(CART);
    if (!cart) {
      return;
    }
    if (document.body.classList.contains(CART_OPEN)) {
      show(cart);
      // A slider inside it keeps its own hidden slides out.
      cart.querySelectorAll(SLIDE).forEach(sync);
      dressCart(cart);
    } else {
      hide(cart);
      cart.removeAttribute('role');
      cart.removeAttribute('aria-modal');
      // The theme gives the drawer itself tabindex="0" when it opens and leaves it there.
      if (cart.getAttribute('tabindex') === '0') {
        cart.setAttribute('tabindex', '-1');
      }
    }
    if (!cartSettling) {
      cartSettling = true;
      setTimeout(settleCart, 0);
    }
  }

  // Where focus belongs once the drawer has come to rest open or closed. Not decided inside the observer: in the
  // middle of a click inside the drawer the theme has it closed for a moment (see the top of this file), and moving
  // focus then took it out of the order note and the quantity field.
  function settleCart() {
    var cart = document.querySelector(CART);
    var open = document.body.classList.contains(CART_OPEN);
    var active = document.activeElement;
    cartSettling = false;
    if (!cart || open === cartWasOpen) {
      return;
    }
    cartWasOpen = open;
    if (open) {
      // A dialog takes focus when it opens; the first Tab is then already inside it.
      if (cartIsOpen(cart) && !cart.contains(active)) {
        cart.setAttribute('tabindex', '-1');
        cart.focus({ preventScroll: true });
      }
      return;
    }
    // Closed: focus goes back to where it was before the drawer took it, unless the visitor has moved on.
    if (active && active !== document.body && !cart.contains(active)) {
      return;
    }
    if (lastOutsideCart && document.contains(lastOutsideCart)) {
      lastOutsideCart.focus({ preventScroll: true });
    } else if (active && cart.contains(active)) {
      // Nothing to return to: focus does not stay on a control of the closed drawer, off screen.
      active.blur();
    }
  }

  function text(element) {
    return element ? element.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  // The bottom of whatever is fixed or sticky at the top of the window (nothing on this theme today: its header
  // scrolls away), so an open list is not scrolled under it.
  function coverTop() {
    var cover = 0;
    document.elementsFromPoint(window.innerWidth / 2, 1).forEach(function (element) {
      for (var node = element; node && node !== document.body; node = node.parentElement) {
        var position = window.getComputedStyle(node).position;
        if (position === 'fixed' || position === 'sticky') {
          cover = Math.max(cover, node.getBoundingClientRect().bottom);
          break;
        }
      }
    });
    return cover;
  }

  // An open list that reaches below the window is scrolled into view, as far as its field stays in sight.
  function intoView(field, panel) {
    var over = panel.getBoundingClientRect().top + panel.offsetHeight + 12 - window.innerHeight;
    var room = field.getBoundingClientRect().top - coverTop() - SELECT_GAP;
    if (over > 0 && room > 0) {
      window.scrollBy({
        top: Math.min(over, room),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }
  }

  // Slim-select as a combobox: the visible field is the stop; its search field of no height keeps the keys of the
  // open list (the library's own: arrows, Enter, Escape, Tab) and the name of the list.
  function dressSelect(input) {
    var main = input.closest('.ss-main');
    var select = main && main.previousElementSibling;
    if (!select || select.tagName !== 'SELECT') {
      return;
    }
    var name = select.getAttribute('aria-label') || text(select.labels && select.labels[0]);
    if (!name) {
      // The product page's option list: the theme's label points at an id it never prints
      // (snippets/product-option-row.liquid), so the list has no name of its own. Its caption is in the same row.
      var row = select.closest(OPTION_ROW);
      name = text(row && row.querySelector('label'));
    }
    if (name) {
      input.setAttribute('aria-label', name);
    }
    var slim = select.slim;
    var field = main.querySelector(SELECT_FIELD);
    var panel = main.querySelector(SELECT_PANEL);
    var list = main.querySelector(SELECT_LIST);
    if (!slim || !field || !panel || !list || field.getAttribute('role') === 'combobox') {
      return;
    }
    var keptByKeys = false;
    var openedByEnter = false;
    var rowChosen = false;
    var beforeOpen = slim.beforeOpen;
    var afterOpen = slim.afterOpen;
    var afterClose = slim.afterClose;
    selects += 1;
    list.id = list.id || 'almila-select-list-' + selects;
    field.setAttribute('role', 'combobox');
    field.setAttribute('tabindex', '0');
    field.setAttribute('aria-haspopup', 'listbox');
    field.setAttribute('aria-expanded', 'false');
    field.setAttribute('aria-controls', list.id);
    if (name) {
      field.setAttribute('aria-label', name);
    }
    // The list is named by its field (aria-labelledby wins over the label the library writes, which
    // almila-media-labels.js turns into "Опции").
    field.id = field.id || list.id + '-field';
    list.setAttribute('aria-labelledby', field.id);
    // Focus on the search field opens the list, so it is reached from the visible field, not by Tab. The list
    // scrolls, and a scrolling box is a stop of its own in Chrome.
    input.setAttribute('tabindex', '-1');
    list.setAttribute('tabindex', '-1');
    // While the list is open the search field has focus: it is the combobox a screen reader hears, and its active
    // descendant is the row under the arrows (the library only moves a class, so the rows were walked in silence).
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', list.id);
    var walking = false;
    var syncRows = function () {
      var highlighted = null;
      list.querySelectorAll(SELECT_OPTION).forEach(function (option, index) {
        option.id = list.id + '-' + index;
        option.setAttribute('aria-selected', option.classList.contains('ss-option-selected') ? 'true' : 'false');
        if (option.classList.contains('ss-highlighted')) {
          highlighted = option;
        }
      });
      if (!highlighted) {
        input.removeAttribute('aria-activedescendant');
        return;
      }
      input.setAttribute('aria-activedescendant', highlighted.id);
      // The row under the arrows stays in the window (on the larger collections the list does not scroll in itself).
      // Only for the arrows: on opening, the whole list is brought into view instead (intoView).
      var box = highlighted.getBoundingClientRect();
      if (walking && (box.bottom > window.innerHeight || box.top < 0)) {
        highlighted.scrollIntoView({ block: 'nearest' });
      }
    };
    syncRows();
    new MutationObserver(syncRows).observe(list, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    // The list opens on the current row: the library starts from the first row, or from the row left marked when the
    // list was last closed, and a second Enter chose it.
    input.addEventListener('focus', function () {
      var chosen = list.querySelector('.ss-option-selected');
      walking = false;
      if (chosen) {
        list.querySelectorAll('.ss-highlighted').forEach(function (option) {
          option.classList.remove('ss-highlighted');
        });
        chosen.classList.add('ss-highlighted');
      }
    });
    field.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openedByEnter = event.key === 'Enter';
        input.focus();
      }
    });
    // The Enter that opened the list is released on the search field, where the library takes a released Enter for
    // a choice: it would choose the row left marked from the last visit (on the sort list, and reload the page).
    input.addEventListener('keyup', function (event) {
      if (openedByEnter && event.key === 'Enter') {
        event.stopImmediatePropagation();
      }
      openedByEnter = false;
    }, true);
    // Enter (a choice) and Escape close the list and keep the visitor on the field; Tab closes it and moves on. The
    // field stands before the search field in the page, so Shift+Tab is sent on from the field: from the search field
    // it landed on the field itself.
    input.addEventListener('keydown', function (event) {
      keptByKeys = event.key === 'Enter' || event.key === 'Escape' || event.key === 'Esc';
      walking = event.key === 'ArrowDown' || event.key === 'ArrowUp';
      if (event.key === 'Tab' && event.shiftKey) {
        field.focus({ preventScroll: true });
      }
    });
    // A closed list is only scaled to nothing by the library's CSS, so a screen reader still walked every row (and
    // met the search field as a second combobox). The panel is out of the accessibility tree while the list is
    // closed: aria-hidden, not inert or hidden, because focus on the search field inside it is what opens the list.
    // It comes off in beforeOpen, which the library runs before it shows the panel (from the keys, inside the focus
    // that lands in the search field, so before the accessibility tree is next read), and goes back on in afterClose,
    // once the library has taken focus out of the search field: a hidden panel never holds focus.
    // After a choice focus is on the field, as on a native select, whatever chose the row: a click left focus on the
    // list (tabindex -1), out of sight, and a screen reader's activation of a row left it on nothing, its reading
    // place inside the panel that is now hidden. The library tells the select of a choice with a change event.
    select.addEventListener('change', function () {
      rowChosen = true;
    });
    var conceal = function () {
      // Opened again before the last close had run to its end (both wait the library's timeoutDelay).
      if (panel.classList.contains(SELECT_OPEN)) {
        return;
      }
      var active = document.activeElement;
      if (panel.contains(active) || (rowChosen && (!active || active === document.body))) {
        field.focus({ preventScroll: true });
      }
      rowChosen = false;
      panel.setAttribute('aria-hidden', 'true');
    };
    conceal();
    slim.beforeOpen = function () {
      rowChosen = false;
      panel.removeAttribute('aria-hidden');
      if (beforeOpen) {
        beforeOpen.call(slim);
      }
    };
    slim.afterOpen = function () {
      field.setAttribute('aria-expanded', 'true');
      input.setAttribute('aria-expanded', 'true');
      intoView(field, panel);
      if (afterOpen) {
        afterOpen.call(slim);
      }
    };
    // The library blurs its search field when the list closes, which left focus on nothing.
    slim.afterClose = function () {
      var active = document.activeElement;
      field.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-expanded', 'false');
      if (keptByKeys && (!active || active === document.body || main.contains(active))) {
        field.focus({ preventScroll: true });
      }
      keptByKeys = false;
      conceal();
      if (afterClose) {
        afterClose.call(slim);
      }
    };
  }

  function hiddenSearch(root) {
    if (root.matches && root.matches(HIDDEN_SEARCH)) {
      dressSelect(root);
    }
    root.querySelectorAll(HIDDEN_SEARCH).forEach(dressSelect);
  }

  function headerStops(root) {
    root.querySelectorAll(HEADER_STOPS).forEach(function (container) {
      container.removeAttribute('tabindex');
    });
  }

  // A control's own place in the Tab order: hide() parks a hidden slide's controls at -1 and keeps their own value.
  function stops(slide) {
    return Array.prototype.filter.call(slide.querySelectorAll(FOCUSABLE), function (el) {
      var own = el.hasAttribute(SAVED) ? el.getAttribute(SAVED) : el.getAttribute('tabindex');
      return own !== '-1' && !el.disabled && el.getClientRects().length > 0;
    });
  }

  function hiddenSlide(slide) {
    return slide.getAttribute('aria-hidden') === 'true';
  }

  // tiny-slider names the row an arrow moves (aria-controls) and the arrow's direction (data-controls).
  function rowArrow(row, direction) {
    if (!row.id) {
      return null;
    }
    return document.querySelector('[data-controls="' + direction + '"][aria-controls="' + CSS.escape(row.id) + '"]');
  }

  // Handed the box around a row's arrows (the complementary row), tiny-slider makes the box the Tab stop and the
  // arrows not. The arrows are placed over the photos, so the box has no height and its focus showed nowhere.
  function rowArrowBox(row) {
    var arrow = rowArrow(row, 'next');
    var box = arrow && arrow.parentElement;
    if (box && box.getAttribute('tabindex') === '0') {
      box.setAttribute('tabindex', '-1');
    }
  }

  // Moves a card row with its own arrows until the slider counts the card in view and, as far as the slider can move,
  // the card stands inside the row's box. The slide-in is off meanwhile, so each step can be measured at once. The
  // theme's grid gives the complementary row's cards at least 300px, wider than a step from 768 to 959px and at 320px,
  // and there the slider's count and the box disagree: a card counted out can stand inside the box. So a counted-out
  // card is first stepped towards, whatever the box shows; then the steps are measured, and a measured step that
  // would push the card's start out of the box or count the card out is taken back. A step only while the slider can
  // still move that way (its first or last card hidden): the complementary row's arrows wrap round at either end.
  function showCard(row, card) {
    var clip = row.closest('.tns-ovh');
    var prev = rowArrow(row, 'prev');
    var next = rowArrow(row, 'next');
    var cards = Array.prototype.slice.call(row.children);
    var at = cards.indexOf(card);
    var content = card.firstElementChild || card;
    if (!clip || !prev || !next || at < 0) {
      return;
    }
    if (clip.scrollLeft) {
      clip.scrollLeft = 0;
    }
    var box = clip.getBoundingClientRect();
    var before = function () {
      return content.getBoundingClientRect().left < box.left - 1;
    };
    var after = function () {
      return content.getBoundingClientRect().right > box.right + 1;
    };
    // No card before this one is counted in view: the card stands before the slider's view.
    var earlier = function () {
      for (var i = 0; i < at; i += 1) {
        if (!hiddenSlide(cards[i])) {
          return false;
        }
      }
      return true;
    };
    row.style.setProperty('transition-property', 'none', 'important');
    for (var steps = cards.length * 2; steps > 0; steps -= 1) {
      if (hiddenSlide(card)) {
        (earlier() ? prev : next).click();
      } else if (before() && hiddenSlide(cards[0])) {
        prev.click();
        if (hiddenSlide(card)) {
          next.click();
          break;
        }
      } else if (after() && hiddenSlide(cards[cards.length - 1])) {
        next.click();
        if (before() || hiddenSlide(card)) {
          prev.click();
          break;
        }
      } else {
        break;
      }
    }
    // The last step is laid out before the slide-in is back, so it does not play afterwards.
    row.getBoundingClientRect();
    row.style.removeProperty('transition-property');
  }

  // Focus that arrives in a card row from the keyboard (Tab, a screen reader's cursor) brings its card in before the
  // browser would scroll the row's box to it. Not after a pointer press: the row would move under a click.
  function rowFocus(event) {
    var slide = event.target && event.target.closest ? event.target.closest(SLIDE) : null;
    var row = slide && slide.parentElement;
    if (row && row.matches(CARD_ROW) && !document.documentElement.hasAttribute(POINTER)) {
      showCard(row, slide);
    }
  }

  // Tab on the last stop of the last card in view (Shift+Tab: the first of the first): the next card is hidden and
  // its controls are out of the Tab order, so the row brings it in first and focus goes to it. Any other Tab is the
  // browser's. Option+Tab counts too: Safari on a Mac reaches links with it.
  function rowTab(event) {
    var active = document.activeElement;
    if (event.key !== 'Tab' || event.ctrlKey || event.metaKey || !active || !active.closest) {
      return;
    }
    var slide = active.closest(SLIDE);
    var row = slide && slide.parentElement;
    if (!row || !row.matches(CARD_ROW)) {
      return;
    }
    var back = event.shiftKey;
    var own = stops(slide);
    if (active !== own[back ? 0 : own.length - 1]) {
      return;
    }
    var card = slide;
    var cardStops = [];
    while (card && !cardStops.length) {
      card = back ? card.previousElementSibling : card.nextElementSibling;
      cardStops = card ? stops(card) : [];
    }
    if (!card || !hiddenSlide(card)) {
      return;
    }
    showCard(row, card);
    if (hiddenSlide(card)) {
      return;
    }
    event.preventDefault();
    // The theme's own Tab handlers on the document never see this Tab.
    event.stopPropagation();
    cardStops[back ? cardStops.length - 1 : 0].focus();
  }

  // A card can stand partly outside the row's box where the slider cannot move further (the complementary row's last
  // card from 768 to 959px), and the browser then scrolls the box to the focused control. Once focus has left the
  // row, the box is put back in line with the slider.
  function rowLeave(event) {
    var clip = event.target && event.target.closest ? event.target.closest('.tns-ovh') : null;
    // The window losing focus keeps the control focused (it is focused again on return): the box stays.
    var moved = document.activeElement !== event.target;
    var staying = !moved || (event.relatedTarget && clip && clip.contains(event.relatedTarget));
    if (clip && clip.scrollLeft && !staying && clip.querySelector(CARD_ROW)) {
      clip.scrollLeft = 0;
    }
  }

  function syncWithin(root) {
    if (root.nodeType !== 1) {
      return;
    }
    // The cart's contents are drawn again after every change to the cart.
    if (root.closest(CART)) {
      syncCart();
    }
    // Slim-select builds its list after the page has loaded.
    hiddenSearch(root);
    var badge = root.closest('#' + BADGE) || root.querySelector('#' + BADGE);
    if (badge) {
      labelBadge(badge);
    }
    var owner = root.closest(SLIDE);
    if (owner) {
      sync(owner);
    }
    root.querySelectorAll(SLIDE).forEach(sync);
    // A card row is built after the page has loaded (the complementary row once its cards have arrived).
    var row = root.closest(CARD_ROW);
    if (row) {
      rowArrowBox(row);
    }
    root.querySelectorAll(CARD_ROW).forEach(rowArrowBox);
  }

  // The theme shows #back-top after 300px of scrolling (app.js backToTop). Here it steps aside while the page moves
  // down and returns once the page has moved BACK_TOP_RETURN px up from the lowest point reached. The position is
  // held to the page's scroll range, so the bounce at either end of an iPhone's page does not count as a move.
  function backTop() {
    var button = document.getElementById(BACK_TOP);
    if (!button) {
      return;
    }
    var last = window.scrollY;
    var deepest = last;
    var away = false;
    window.addEventListener('scroll', function () {
      var end = document.documentElement.scrollHeight - window.innerHeight;
      var y = Math.max(Math.min(window.scrollY, end), 0);
      var next = away;
      if (y > last) {
        deepest = y;
        next = true;
      } else if (deepest - y >= BACK_TOP_RETURN) {
        next = false;
      }
      if (next !== away) {
        away = next;
        button.classList.toggle(BACK_TOP_AWAY, away);
      }
      last = y;
    }, { passive: true });
  }

  function observe() {
    syncWithin(document.body);
    document.querySelectorAll(ACCORDION).forEach(accordion);
    headerStops(document);
    syncCart();
    new MutationObserver(syncCart).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    // On the document, before the theme's own handler on the link: Tab then goes where the page goes.
    document.addEventListener('keydown', function (event) {
      var target = event.target;
      if (event.key === 'Tab' && target && target.classList && target.classList.contains(SKIP_LINK)) {
        event.stopPropagation();
      }
    }, true);
    // Tab pressed inside the product gallery stops at <body>: after the gallery's own handlers, before the theme's
    // redirects on the document (written for its own layout; with ours they looped inside the gallery).
    document.body.addEventListener('keydown', function (event) {
      var target = event.target;
      if (event.key === 'Tab' && target && target.closest && target.closest(GALLERY)) {
        event.stopPropagation();
      }
    });
    // The theme's card rows follow keyboard focus (rowFocus, rowTab, rowLeave above). Heard first, before the theme's
    // own Tab handlers on the document.
    document.addEventListener('keydown', rowTab, true);
    document.addEventListener('focusin', rowFocus, true);
    document.addEventListener('focusout', rowLeave, true);
    // The open cart drawer keeps Tab inside and closes on Escape. Heard first, so the theme's own Tab handler
    // (which looks for sliders that are not there and throws) never runs for it.
    document.addEventListener('keydown', function (event) {
      var cart = document.querySelector(CART);
      if (!cartIsOpen(cart)) {
        return;
      }
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        closeCart(cart);
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      event.stopPropagation();
      var items = cartControls(cart);
      var active = document.activeElement;
      if (!items.length) {
        event.preventDefault();
        cart.focus();
      } else if (!cart.contains(active)) {
        event.preventDefault();
        items[0].focus();
      } else if (event.shiftKey && (active === items[0] || active === cart)) {
        event.preventDefault();
        items[items.length - 1].focus();
      } else if (!event.shiftKey && active === items[items.length - 1]) {
        event.preventDefault();
        items[0].focus();
      }
    }, true);
    // The theme closes the drawer when focus leaves its tax note, then focuses a menu item this page does not have.
    document.addEventListener('focusout', function (event) {
      var target = event.target;
      if (target && target.matches && target.matches(CART_NOTE) && target.closest(CART)) {
        event.stopPropagation();
      }
    }, true);
    document.addEventListener('focusin', function (event) {
      var cart = document.querySelector(CART);
      if (cart && event.target && !cart.contains(event.target)) {
        lastOutsideCart = event.target;
      }
    }, true);
    // The ring on a slim-select field is for the keyboard (almila-a11y.css, almila-product-layout.css): a pointer
    // press marks the page until Tab is used again. A text field always counts as :focus-visible, so the
    // stylesheet alone cannot tell a click from a key.
    document.addEventListener('pointerdown', function () {
      if (!document.documentElement.hasAttribute(POINTER)) {
        document.documentElement.setAttribute(POINTER, '');
      }
    }, true);
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Tab') {
        document.documentElement.removeAttribute(POINTER);
      }
    }, true);
    hiddenSearch(document.body);
    backTop();
    new MutationObserver(function (records) {
      records.forEach(function (record) {
        if (record.type === 'attributes') {
          if (record.target.matches(SLIDE)) {
            sync(record.target);
          }
          return;
        }
        record.addedNodes.forEach(syncWithin);
      });
    }).observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
})();
